import React, { useMemo, useState, useEffect } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    Box,
    SimpleGrid,
    Button,
    Icon,
    useToast,
} from '@chakra-ui/react'
import { WarningIcon } from '../../assets/icons'
import isEmpty from 'validator/lib/isEmpty'
import { addAsset, updateAsset } from '../../api/assets/assets'
import { fetchEmployeeList } from '../../api/workforce/workforce'
import handleToast from '../HandleToast'
import ModalInput from '../forms/modalForms/modalInput'
import ModalSelect from '../forms/modalForms/modalSelect'
import { useTranslation } from 'react-i18next'
import { modal, button } from '../../themes/styles'

interface AddAssetsModalProps {
    isOpen: boolean
    onClose: () => void
    asset?: any // Optional asset for editing
    preselectedEmployeeId?: string // Optional preselected employee ID for locking assignee
    preselectedEmployee?: { id: string; first_name: string; last_name: string; profile_picture?: string }
    disableAssignee?: boolean // Optional flag to disable assignee dropdown when editing from user profile
}

const AddAssetsModal: React.FC<AddAssetsModalProps> = ({ isOpen, onClose, asset, preselectedEmployeeId, preselectedEmployee, disableAssignee }) => {
    const toast = useToast()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    type AssetType = 'computer' | 'office' | 'phone' | 'key' | 'vehicle' | 'other';
    type AssetStatus = 'handed_out' | 'handed_in' | 'maintenance';

    const [assetState, setAssetState] = useState<{
        type: AssetType;
        description: string;
        company_id: string;
        serial_number: string;
        status: AssetStatus;
        comment: string;
        date_handed_out: string;
        date_handed_in: string;
        assignee: any;
    }>(
        {
            type: 'computer',
            description: '',
            company_id: '',
            serial_number: '',
            status: 'handed_out',
            comment: '',
            date_handed_out: '',
            date_handed_in: '',
            assignee: preselectedEmployeeId || '',
        }
    );

    // Prefill form if editing
    useEffect(() => {
        if (asset) {
            setAssetState({
                type: asset.type || 'computer',
                description: asset.description || '',
                company_id: asset.company_id || '',
                serial_number: asset.serial_number || '',
                status: asset.status || 'handed_out',
                comment: asset.comment || '',
                date_handed_out: asset.date_handed_out || '',
                date_handed_in: asset.date_handed_in || '',
                assignee: asset.assigned_to && asset.assigned_to.id ? String(asset.assigned_to.id) : '',
            });
        } else {
            setAssetState({
                type: 'computer',
                description: '',
                company_id: '',
                serial_number: '',
                status: 'handed_out',
                comment: '',
                date_handed_out: '',
                date_handed_in: '',
                assignee: preselectedEmployeeId || '',
            });
        }
    }, [asset, isOpen, preselectedEmployeeId]);

    const [errors, setErrors] = useState({
        type: false,
        description: false,
        company_id: false,
        serial_number: false,
        status: false,
        comment: false,
        date_handed_out: false,
        date_handed_in: false,
        assignee: false
    })


    const [memberSearch, setMemberSearch] = useState('')
    const pageSize = 10

    const membersQuery = useInfiniteQuery({
        queryKey: ['teamMembers', memberSearch, pageSize],
        queryFn: ({ pageParam }) => fetchEmployeeList({
            search: memberSearch,
            page: pageParam,
            pageSize: pageSize,
            filters: {},
            minimal: true,
        }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
        enabled: isOpen, // Keep enabled so we can search even in edit/create
    })

    const membersList = useMemo(() => {
        const pages = membersQuery.data?.pages ?? []
        return pages.flatMap((p: any) => (Array.isArray(p?.data) ? p.data : []))
    }, [membersQuery.data?.pages])

    const memberOptionsBase = useMemo(
        () =>
            membersList.map((member: any) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
                profilePicture: member.profile_picture,
            })),
        [membersList]
    )

    const memberOptions = useMemo(() => {
        let options = [...memberOptionsBase]

        // Inject asset.assigned_to into options if not present
        if (asset && asset.assigned_to && asset.assigned_to.id && isOpen) {
            const exists = options.some((opt: any) => String(opt.value) === String(asset.assigned_to.id))
            if (!exists) {
                options = [
                    {
                        value: String(asset.assigned_to.id),
                        label: `${asset.assigned_to.first_name} ${asset.assigned_to.last_name}`,
                        profilePicture: asset.assigned_to.profile_picture,
                    },
                    ...options,
                ]
            }
        }

        // Inject preselectedEmployee into options if provided
        if (preselectedEmployee && preselectedEmployeeId && isOpen) {
            const exists = options.some((opt: any) => String(opt.value) === String(preselectedEmployeeId))
            if (!exists) {
                options = [
                    {
                        value: String(preselectedEmployee.id),
                        label: `${preselectedEmployee.first_name} ${preselectedEmployee.last_name}`,
                        profilePicture: preselectedEmployee.profile_picture,
                    },
                    ...options,
                ]
            }
        }

        return options
    }, [memberOptionsBase, asset, isOpen, preselectedEmployee, preselectedEmployeeId])

    const createAssetMutation = useMutation({
        mutationFn: addAsset,
        onSuccess: () => {
            handleToast(toast, true, t('workforce.assets.messages.createdTitle'), t('workforce.assets.messages.createdMessage'))
            queryClient.invalidateQueries({ queryKey: ['assets'] })
            handleClose()
        },
        onError: (error: Error) => {
            handleToast(toast, false, t('workforce.assets.messages.createFailedTitle'), error.message)
        },
    })

    const editAssetMutation = useMutation({
        mutationFn: ({ id, data }: { id: string | number; data: any }) => updateAsset(id, data),
        onSuccess: () => {
            handleToast(toast, true, t('workforce.assets.messages.updatedTitle'), t('workforce.assets.messages.updatedMessage'))
            queryClient.invalidateQueries({ queryKey: ['assets'] })
            handleClose()
        },
        onError: (error: Error) => {
            handleToast(toast, false, t('workforce.assets.messages.updateFailedTitle'), error.message)
        },
    })

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setAssetState((prev) => ({ ...prev, [name]: value }));
    }

    const isValid = () => {
        const newErrors = {
            type: isEmpty(assetState.type.trim()),
            assignee: isEmpty(assetState.assignee.trim()),
            description: false,
            company_id: false,
            serial_number: false,
            status: false,
            comment: false,
            date_handed_out: false,
            date_handed_in: false
        }
        setErrors(newErrors)
        return !Object.values(newErrors).some((error) => error)
    }

    const handleSubmit = () => {
        if (!isValid()) return;
        const payload: any = {
            type: assetState.type,
            description: assetState.description,
            company_id: assetState.company_id,
            serial_number: assetState.serial_number,
            status: assetState.status,
            comment: assetState.comment,
            assigned_to: assetState.assignee
        };
        if (assetState.date_handed_out) {
            payload.date_handed_out = assetState.date_handed_out;
        }
        if (assetState.date_handed_in) {
            payload.date_handed_in = assetState.date_handed_in;
        }
        if (asset && asset.id) {
            editAssetMutation.mutate({ id: asset.id, data: payload });
        } else {
            createAssetMutation.mutate(payload);
        }
    }

    const handleClose = () => {
        onClose();
        setAssetState({
            type: 'computer',
            description: '',
            company_id: '',
            serial_number: '',
            status: 'handed_out',
            comment: '',
            date_handed_out: '',
            date_handed_in: '',
            assignee: '',
        });
        setErrors({ type: false, description: false, company_id: false, serial_number: false, status: false, comment: false, date_handed_out: false, date_handed_in: false, assignee: false });
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader {...modal.header}>{asset ? t('workforce.assets.modal.editTitle') : t('workforce.assets.modal.addTitle')}</ModalHeader>
                <ModalCloseButton sx={modal.closeButton} />
                <ModalBody>
                    <SimpleGrid columns={2} spacing={4} mb={4}>
                        {/* Type */}
                        <Box width="100%">
                            <ModalSelect
                                label={t('common.fields.type')}
                                name="type"
                                value={assetState.type}
                                options={[
                                    { value: 'computer', label: t('workforce.assets.types.computer') },
                                    { value: 'office', label: t('workforce.assets.types.office') },
                                    { value: 'phone', label: t('workforce.assets.types.phone') },
                                    { value: 'key', label: t('workforce.assets.types.key') },
                                    { value: 'vehicle', label: t('workforce.assets.types.vehicle') },
                                    { value: 'other', label: t('workforce.assets.types.other') },
                                ]}
                                onChange={handleChange}
                                searchDisabled={true}
                            />
                            {errors.type && (
                                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                    <Text>
                                        <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                            <WarningIcon />
                                        </Icon>
                                        {t('validation.required', { field: t('common.fields.type') })}
                                    </Text>
                                </Box>
                            )}
                        </Box>

                        {/* Status */}
                        <Box width="100%">
                            <ModalSelect
                                label={t('common.fields.status')}
                                name="status"
                                value={assetState.status}
                                options={[
                                    { value: 'handed_out', label: t('workforce.assets.statuses.handedOut') },
                                    { value: 'handed_in', label: t('workforce.assets.statuses.handedIn') },
                                    { value: 'maintenance', label: t('workforce.assets.statuses.underMaintenance') },
                                ]}
                                onChange={handleChange}
                                searchDisabled={true}
                            />
                            {errors.status && (
                                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                    <Text>
                                        <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                            <WarningIcon />
                                        </Icon>
                                        {errors.status}
                                    </Text>
                                </Box>
                            )}
                        </Box>


                        {/* Date Handed Out */}
                        <Box width="100%">
                            <ModalInput
                                label={t('workforce.assets.fields.dateHandedOut')}
                                type="date"
                                name="date_handed_out"
                                value={assetState.date_handed_out}
                                onChange={handleChange}
                                isInvalid={!!errors.date_handed_out}
                            />
                            {errors.date_handed_out && (
                                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                    <Text>
                                        <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                            <WarningIcon />
                                        </Icon>
                                        {errors.date_handed_out}
                                    </Text>
                                </Box>
                            )}
                        </Box>

                        {/* Date Handed In */}
                        <Box width="100%">
                            <ModalInput
                                label={t('workforce.assets.fields.dateHandedIn')}
                                type="date"
                                name="date_handed_in"
                                value={assetState.date_handed_in}
                                onChange={handleChange}
                                isInvalid={!!errors.date_handed_in}
                            />
                            {errors.date_handed_in && (
                                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                    <Text>
                                        <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                            <WarningIcon />
                                        </Icon>
                                        {errors.date_handed_in}
                                    </Text>
                                </Box>
                            )}
                        </Box>
                    </SimpleGrid>

                    <Box width="100%" mb={4}>
                        <ModalSelect
                            label={t('common.fields.assignee')}
                            name="assignee"
                            value={assetState.assignee}
                            options={memberOptions}
                            onChange={(e) => {
                                const assigneeId = e.target.value;
                                setAssetState((prev) => ({
                                    ...prev,
                                    assignee: assigneeId
                                }));
                            }}
                            hasProfilePicture={true}
                            onSearch={(term) => setMemberSearch(term)}
                            isLoading={membersQuery.isLoading}
                            isDisabled={!!preselectedEmployeeId || !!disableAssignee}
                            onLoadMore={() => membersQuery.fetchNextPage()}
                            hasMore={!!membersQuery.hasNextPage}
                            isLoadingMore={membersQuery.isFetchingNextPage}
                            placeholder={t('common.labels.searchPlaceholder')}
                            isTop={true}
                        />
                        {errors.assignee && (
                            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                <Text>
                                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                        <WarningIcon />
                                    </Icon>
                                    {t('validation.required', { field: t('common.fields.assignee') })}
                                </Text>
                            </Box>
                        )}
                    </Box>

                    {/* Description */}
                    <Box width="100%" mb={4}>
                        <ModalInput
                            label={t('common.fields.description')}
                            type="textarea"
                            name="description"
                            value={assetState.description}
                            onChange={handleChange}
                            isInvalid={!!errors.description}
                        />
                        {errors.description && (
                            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                <Text>
                                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                        <WarningIcon />
                                    </Icon>
                                    {errors.description}
                                </Text>
                            </Box>
                        )}
                    </Box>

                    {/* Company ID */}
                    <Box width="100%" mb={4}>
                        <ModalInput
                            label={t('workforce.assets.fields.companyId')}
                            type="text"
                            name="company_id"
                            value={assetState.company_id}
                            onChange={handleChange}
                            isInvalid={!!errors.company_id}
                        />
                        {errors.company_id && (
                            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                <Text>
                                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                        <WarningIcon />
                                    </Icon>
                                    {errors.company_id}
                                </Text>
                            </Box>
                        )}
                    </Box>

                    {/* Serial Number */}
                    <Box width="100%" mb={4}>
                        <ModalInput
                            label={t('workforce.assets.fields.serialNumber')}
                            type="text"
                            name="serial_number"
                            value={assetState.serial_number}
                            onChange={handleChange}
                            isInvalid={!!errors.serial_number}
                        />
                        {errors.serial_number && (
                            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                <Text>
                                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                        <WarningIcon />
                                    </Icon>
                                    {errors.serial_number}
                                </Text>
                            </Box>
                        )}
                    </Box>
                    {/* Comment */}
                    <Box width="100%">
                        <ModalInput
                            label={t('workforce.assets.fields.comment')}
                            type="textarea"
                            name="comment"
                            value={assetState.comment}
                            onChange={handleChange}
                            isInvalid={!!errors.comment}
                        />
                        {errors.comment && (
                            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                                <Text>
                                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                                        <WarningIcon />
                                    </Icon>
                                    {errors.comment}
                                </Text>
                            </Box>
                        )}
                    </Box>

                </ModalBody>
                <ModalFooter {...modal.footer}>
                    <Button {...button.secondary} onClick={handleClose}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button {...button.primary} onClick={handleSubmit} isLoading={createAssetMutation.isPending || editAssetMutation.isPending}>
                        {asset ? t('common.actions.save') : t('common.actions.submit')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal >
    )
}

export default AddAssetsModal
